import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { CodeoSkill } from '../types';
import { check, splitsTheAxes } from '../skillUsage';

export const arcPathMotionSkill: CodeoSkill = {
    name: SKILL_NAMES.arcPathMotion,
    kind: 'codeo',
    description:
        'Move things along arcs, not straight lines, by animating the two axes separately: a ' +
        'linear horizontal and an eased vertical, so a tossed or bouncing element hangs at the ' +
        'top of each arc and hits the floor fast.',
    checkLooksFor:
        'the horizontal and vertical travel as separate tweens — x on one element or tween, y on another — so their eases differ',
    documentUsesSkill: check((script) => splitsTheAxes(script)),
    instructions: `# Arcs

Natural movement travels on arcs. An arc is not a curved path you draw: it is **two simple motions at once**, one per axis. The horizontal moves at a constant speed, with a linear ease. The vertical has a fast, sharp ease: every rise eases out into its apex and every fall eases in onto the floor. Put together they trace curves that **hang at the top and turn sharply at the bottom**. That is how a thrown thing looks, and why one tween that moves x and y together on one ease looks like a machine sliding on a rail.

## How it is built

- **Nest the axes.** An outer wrapper moves only in x, an inner wrapper moves only in y, and the visible element sits inside. Each axis then has its own tweens and its own ease, and the element itself stays free for rotation or deformation (\`${SKILL_NAMES.squashAndStretch}\`).
- **x is one linear tween across the whole path.** Constant speed, from where the element enters to where it finally rests, through every hop. Because x is linear, horizontal distance is time: each apex sits halfway along its hop.
- **y is a chain of half-hops.** Rise from the floor to the apex, eased out; fall from the apex to the floor, eased in, as the rise played backwards.
- **The curves are sharper for lower hops.** Tall hops ease like a cubic; the low hops at the end are sharper still, so they snap off the floor and drop back.
- **It bounces off a visible surface.** A contact with nothing under it looks like the element hitting thin air. Draw what it lands on: a floor line, the top edge of a shape or panel, or the frame's bottom edge. The surface sits exactly where the element's bottom is at contact, runs under every contact along the path, and is strong enough to read at a glance; a faint hairline does not count. A contact shadow does not count as the surface on its own. If you add one, it travels with the x wrapper so it stays under the element.
- **Each hop is lower and quicker.** Measured from a five-hop bounce: heights 1, 0.72, 0.50, 0.19, 0.08 of the first, lasting 0.67, 0.53, 0.40, 0.27, 0.27s. Height falls much faster than time: the last two hops last the same.

## Markup

\`\`\`html
<div id="arc-stage" style="position: absolute; inset: 0; display: grid; place-items: center; overflow: hidden;">
  <div id="arc-floor" data-l5-absolute style="position: absolute; left: 0; right: 0; top: calc(50% + 90px); height: 8px; background: var(--l5-color-0, #101820);"></div>
  <div id="arc-x" style="grid-area: 1 / 1;">
    <div id="arc-y">
      <div id="arc-object" style="width: 180px; height: 180px; border-radius: 50%; background: var(--l5-color-2, #ff5a36);"></div>
    </div>
  </div>
</div>
\`\`\`

## Timeline code

The element drops in from above the frame on the falling half of a tall arc, then hops into its resting place, each hop lower and quicker.

\`\`\`js
const tl = gsap.timeline({ paused: true });

// Fitted to a hand-animated bounce. Each fall is its rise played backwards.
CustomEase.create('arcRise', 'M0,0 C0.17,0.17 0.26,1 1,1');
CustomEase.create('arcFall', 'M0,0 C0.74,0 0.83,0.83 1,1');
CustomEase.create('arcRiseLow', 'M0,0 C0.17,0.17 0.01,1 1,1');
CustomEase.create('arcFallLow', 'M0,0 C0.99,0 0.83,0.83 1,1');

const LAND = 0.6;        // first contact: the word it lands on
const SPEED_X = 650;     // px/s, the same for the whole path
const CONTACT = 0.045;   // one frame on the floor, so every contact is seen
const ENTRY = { height: 640, fall: 0.36 };
// Contact to contact.
const HOPS = [
    { height: 300, duration: 0.46, rise: 'arcRise', fall: 'arcFall' },
    { height: 120, duration: 0.3, rise: 'arcRiseLow', fall: 'arcFallLow' },
    { height: 50, duration: 0.25, rise: 'arcRiseLow', fall: 'arcFallLow' },
];

const START = LAND - ENTRY.fall;
const REST = HOPS.reduce((at, hop) => at + hop.duration, LAND);

gsap.set('#arc-x', { x: -SPEED_X * (REST - START) });
gsap.set('#arc-y', { y: -ENTRY.height });

// x: one linear tween across the whole path.
tl.to('#arc-x', { x: 0, duration: REST - START, ease: 'none' }, START);

// y: each rise eases out into its apex, each fall eases in onto the floor.
tl.to('#arc-y', { y: 0, duration: ENTRY.fall, ease: 'arcFall' }, START);
let at = LAND;
HOPS.forEach((hop) => {
    const half = (hop.duration - CONTACT) / 2;
    tl.to('#arc-y', { y: -hop.height, duration: half, ease: hop.rise }, at + CONTACT);
    tl.to('#arc-y', { y: 0, duration: half, ease: hop.fall }, at + CONTACT + half);
    at += hop.duration;
});

tl.set({}, {}, REST + 0.6);
window.__timelines = { main: tl };
\`\`\`

## Adapt

- **A toss.** One rise from below the frame to an apex, one fall onto the resting spot, then two small hops. Keep x fast enough that the rise travels sideways at least half as far as it climbs, or the toss shoots straight up like a fountain.
- **Thrown out.** Rise on \`arcRise\`, then fall out of the bottom of the frame on \`arcFall\`, with x continuing at the same speed. The apex is where the eye catches it, so put it on the frame's upper third.
- **A move with no gravity.** Something sliding from one place to another still reads better on a bowed path. Give x and y different curves (x on \`sine.inOut\`, y on \`power3.out\`) so one axis finishes earlier and the path curves.
- **Weight.** How high and how often it rebounds says how heavy it is: \`${SKILL_NAMES.massAndWeight}\` has the tables. The heights above are a light, lively bounce.
- **Deformation.** Stretch along the path near the floor, squash on contact: \`${SKILL_NAMES.squashAndStretch}\`, which builds on exactly this structure.

## Pitfalls

- **x that stops at the first landing** leaves every later hop going straight up and down: the element bounces in place and the arcs are gone. x runs until the last contact.
- **Never slow it to fill a scene.** These durations are the gravity. Stretched, the element drifts like a balloon; extra slow hops added after it rests read the same way. The path takes its own time and the element then holds still.
- **Start it moving.** An element visible before its path starts hangs in mid-air. Begin outside the frame, or on the floor, so the first frame it appears it is already travelling.
- **Use the fitted curves**, not \`power3\` or \`power4\`. Those are sharper than the measured bounce: they leave the floor faster and sit at the top longer.
- **One tween carrying both x and y on one ease is a straight line.** The arc only exists because the axes have different eases.
- **An eased x on a bounce** slows the element at every contact and makes it stick to the floor. The horizontal stays linear until it rests.
- **The drawn edge touches, not the box.** The element's box has to end where its drawing ends. An SVG whose viewBox leaves room below the shape, or an image with a transparent margin, rests that far above the floor and never touches it. Crop the viewBox to the shape, or offset the element by the margin, so the drawn bottom is the box's bottom, then look at a still on a contact frame.
- **Keep the one-frame contact.** A sharp fall reaches the floor between frames, and without the contact hold a render can skip the touch so it bounces off the air.
- **Rounded bottoms are wrong.** If the element eases into the floor (an \`.out\` on a fall) it floats down and lifts off gently, which reads as a balloon on a string, not a bounce. The bottom of each arc is a sharp V; the top is the round part.
- Nesting matters: tween x on the outer wrapper and y on the inner one. Two tweens on one element's \`x\` and \`y\` also work, but the wrappers keep the element free for rotation and scale.`,
};
